import { useState, useEffect } from 'react';
import type { Stats, WeaponType } from '@oz/rules';
import {
  fetchAdminContent,
  saveAdminContent,
  deleteAdminContent,
  type AdminItem,
} from '../../admin/contentApi.js';
import { ContentOrigin } from '../../admin/ContentOrigin.js';
import { WEAPON_TYPE_OPTIONS, archetypeOptions } from '../../admin/vocab.js';

export interface ClassDef {
  readonly id: number;
  readonly name: string;
  readonly archetype: string;
  readonly move: number;
  readonly con: number;
  readonly weaponTypes: readonly WeaponType[];
  readonly bases: Stats;
  readonly growths: Stats;
  readonly promotesTo?: number | null;
  readonly note?: string;
}

const STAT_LABELS: Record<keyof Stats, string> = {
  hp: 'PV (Pontos de Vida)',
  str: 'FOR (Força Física)',
  mag: 'MAG (Poder Mágico)',
  skl: 'HAB (Habilidade / Acerto)',
  spd: 'VEL (Velocidade / Esquiva)',
  lck: 'SOR (Sorte / Crítico)',
  def: 'DEF (Defesa Física)',
  res: 'RES (Resistência Mágica)',
};

const STAT_COLORS: Record<keyof Stats, string> = {
  hp: '#22c55e',
  str: '#ef4444',
  mag: '#a855f7',
  skl: '#3b82f6',
  spd: '#06b6d4',
  lck: '#eab308',
  def: '#f97316',
  res: '#ec4899',
};


const DEFAULT_BASES: Stats = {
  hp: 20,
  str: 5,
  mag: 1,
  skl: 5,
  spd: 5,
  lck: 3,
  def: 4,
  res: 2,
};

const DEFAULT_GROWTHS: Stats = {
  hp: 70,
  str: 45,
  mag: 15,
  skl: 40,
  spd: 45,
  lck: 30,
  def: 30,
  res: 20,
};

export function ClassEditor(): JSX.Element {
  const [items, setItems] = useState<AdminItem<ClassDef>[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [filterText, setFilterText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  // Form states
  const [name, setName] = useState('');
  const [archetype, setArchetype] = useState('mirmidao');
  const [move, setMove] = useState(5);
  const [con, setCon] = useState(7);
  const [weaponTypes, setWeaponTypes] = useState<WeaponType[]>(['espada']);
  const [promotesTo, setPromotesTo] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const [bases, setBases] = useState<Stats>(DEFAULT_BASES);
  const [growths, setGrowths] = useState<Stats>(DEFAULT_GROWTHS);
  const [isNew, setIsNew] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await fetchAdminContent<ClassDef>('classes');
      setItems(data);
      if (data.length > 0 && selectedId === null && data[0]) {
        selectItem(data[0]);
      }
    } catch (e) {
      setMessage({ type: 'err', text: e instanceof Error ? e.message : String(e) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, []);

  const selectItem = (item: AdminItem<ClassDef>) => {
    setSelectedId(item.contentId);
    setIsNew(false);
    setName(item.name || item.data.name || '');
    setArchetype(item.data.archetype || 'mirmidao');
    setMove(item.data.move ?? 5);
    setCon(item.data.con ?? 7);
    setWeaponTypes([...(item.data.weaponTypes || ['espada'])]);
    setPromotesTo(item.data.promotesTo ?? null);
    setNote(item.data.note ?? '');
    setBases(item.data.bases ? { ...item.data.bases } : DEFAULT_BASES);
    setGrowths(item.data.growths ? { ...item.data.growths } : DEFAULT_GROWTHS);
    setMessage(null);
  };

  const handleNew = () => {
    setSelectedId(null);
    setIsNew(true);
    setName('Nova Classe');
    setArchetype('mirmidao');
    setMove(5);
    setCon(7);
    setWeaponTypes(['espada']);
    setPromotesTo(null);
    setNote('Descrição da nova classe.');
    setBases({ ...DEFAULT_BASES });
    setGrowths({ ...DEFAULT_GROWTHS });
    setMessage(null);
  };

  const toggleWeaponType = (wt: WeaponType) => {
    if (weaponTypes.includes(wt)) {
      if (weaponTypes.length === 1) return; // manter ao menos uma
      setWeaponTypes(weaponTypes.filter((t) => t !== wt));
    } else {
      setWeaponTypes([...weaponTypes, wt]);
    }
  };

  const handleBaseChange = (stat: keyof Stats, val: number) => {
    setBases((prev) => ({ ...prev, [stat]: Math.max(0, val) }));
  };

  const handleGrowthChange = (stat: keyof Stats, val: number) => {
    setGrowths((prev) => ({ ...prev, [stat]: Math.max(0, Math.min(200, val)) }));
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setMessage({ type: 'err', text: 'O nome da classe é obrigatório.' });
      return;
    }

    setSaving(true);
    setMessage(null);

    /*
     * O que o formulário não mostra tem de sobreviver ao salvamento.
     *
     * A gravação substitui o registro inteiro pelo que sobe daqui, e há campos
     * que este formulário não edita — de qual jogo importado o registro veio,
     * de qual folha o ícone fala, e o que o conversor trouxe da origem.
     * Montando o pacote em cima da ficha atual, um ajuste de preço deixa de
     * apagá-los.
     */
    const existing = items.find((it) => it.contentId === selectedId)?.data;

    const payload: Partial<ClassDef> & { name: string } = {
      ...(isNew || !existing ? {} : existing),
      name: name.trim(),
      archetype,
      move: Number(move),
      con: Number(con),
      weaponTypes,
      promotesTo: promotesTo ? Number(promotesTo) : null,
      note,
      bases,
      growths,
    };

    const targetId = isNew ? null : selectedId;
    const res = await saveAdminContent<ClassDef>('classes', targetId, payload);

    setSaving(false);
    if (!res.ok) {
      setMessage({ type: 'err', text: res.message ?? 'Erro ao salvar classe.' });
    } else {
      setMessage({ type: 'ok', text: `Classe "${name}" salva com sucesso!` });
      await loadData();
      if (res.item) {
        selectItem(res.item);
      }
    }
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    if (!window.confirm(`Tem certeza que deseja excluir a classe ID #${selectedId} (${name})?`)) {
      return;
    }

    setSaving(true);
    const res = await deleteAdminContent('classes', selectedId);
    setSaving(false);

    if (!res.ok) {
      setMessage({ type: 'err', text: res.message ?? 'Erro ao excluir classe.' });
    } else {
      setMessage({ type: 'ok', text: `Classe #${selectedId} excluída.` });
      const remaining = items.filter((it) => it.contentId !== selectedId);
      setItems(remaining);
      if (remaining.length > 0 && remaining[0]) {
        selectItem(remaining[0]);
      } else {
        handleNew();
      }
    }
  };

  const filteredItems = items.filter(
    (it) =>
      it.name.toLowerCase().includes(filterText.toLowerCase()) ||
      String(it.contentId).includes(filterText) ||
      (it.data.archetype && it.data.archetype.toLowerCase().includes(filterText.toLowerCase())),
  );

  const currentItem = items.find((it) => it.contentId === selectedId);

  return (
    <div className="admin-db-container">
      {/* Coluna Esquerda: Lista Mestre MZ */}
      <aside className="admin-sidebar-list">
        <div className="admin-list-header">
          <input
            type="text"
            className="admin-search-input"
            placeholder="🔍 Filtrar classes..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
          <button type="button" className="admin-btn-primary admin-btn-new" onClick={handleNew}>
            + Nova Classe
          </button>
        </div>

        {loading ? (
          <div className="admin-loading-indicator">Carregando classes…</div>
        ) : (
          <div className="admin-items-scroll">
            {filteredItems.map((it) => {
              const active = !isNew && it.contentId === selectedId;
              return (
                <button
                  key={it.contentId}
                  type="button"
                  className={`admin-list-item-btn ${active ? 'active' : ''}`}
                  onClick={() => selectItem(it)}
                >
                  <span className="admin-item-id">
                    {String(it.contentId).padStart(3, '0')}:
                  </span>
                  <span className="admin-item-name">{it.name}</span>
                  {it.isCustom && <span className="admin-badge-custom">Custom</span>}
                  <span className="admin-item-meta">{it.data.archetype}</span>
                </button>
              );
            })}
          </div>
        )}
      </aside>

      {/* Coluna Direita: Formulário Completo MZ */}
      <main className="admin-main-form">
        <div className="admin-form-top-bar">
          <div className="admin-form-title-area">
            <h2>
              {isNew ? '✨ Criando Nova Classe' : `🛡️ Classe #${selectedId}: ${name}`}
            </h2>
            {currentItem && (
              <ContentOrigin item={currentItem} />
            )}
          </div>

          <div className="admin-form-actions">
            {!isNew && (
              <button
                type="button"
                className="admin-btn-danger"
                onClick={() => void handleDelete()}
                disabled={saving}
              >
                🗑️ Excluir
              </button>
            )}
            <button
              type="button"
              className="admin-btn-save"
              onClick={() => void handleSave()}
              disabled={saving}
            >
              {saving ? 'Salvando…' : '💾 Salvar Alterações'}
            </button>
          </div>
        </div>

        {message && (
          <div className={`admin-alert-banner ${message.type === 'ok' ? 'success' : 'error'}`}>
            {message.text}
          </div>
        )}

        <div className="admin-form-body-grid">
          {/* Caixa 1: Dados Gerais */}
          <div className="admin-section-box">
            <h3 className="admin-section-title">Dados Gerais</h3>
            <div className="admin-form-row">
              <label>Nome da Classe:</label>
              <input
                type="text"
                className="admin-input-text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Paladino Solar"
              />
            </div>

            <div className="admin-form-row">
              <label>Arquétipo / Papel:</label>
              <select
                className="admin-select"
                value={archetype}
                onChange={(e) => setArchetype(e.target.value)}
              >
                {archetypeOptions(archetype).map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="admin-grid-2col">
              <div className="admin-form-row">
                <label>Movimento (Casas):</label>
                <input
                  type="number"
                  min="1"
                  max="12"
                  className="admin-input-number"
                  value={move}
                  onChange={(e) => setMove(Number(e.target.value))}
                />
              </div>

              <div className="admin-form-row">
                <label>Constituição (Peso suportado):</label>
                <input
                  type="number"
                  min="1"
                  max="25"
                  className="admin-input-number"
                  value={con}
                  onChange={(e) => setCon(Number(e.target.value))}
                />
              </div>
            </div>

            <div className="admin-form-row">
              <label>Armas Permitidas:</label>
              <div className="admin-checkbox-group">
                {WEAPON_TYPE_OPTIONS.map((wt) => (
                  <label key={wt.id} className="admin-checkbox-label">
                    <input
                      type="checkbox"
                      checked={weaponTypes.includes(wt.id)}
                      onChange={() => toggleWeaponType(wt.id)}
                    />
                    <span>{wt.icon} {wt.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="admin-form-row">
              <label>
                Promove Para (Classe Avançada):{' '}
                <span className="admin-helper-text">ainda sem efeito — o motor não promove (PLANO §0)</span>
              </label>
              <select
                className="admin-select"
                value={promotesTo ?? ''}
                onChange={(e) => setPromotesTo(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">(Nenhuma / Já é Promovida)</option>
                {items
                  .filter((it) => it.contentId !== selectedId)
                  .map((it) => (
                    <option key={it.contentId} value={it.contentId}>
                      #{it.contentId}: {it.name}
                    </option>
                  ))}
              </select>
            </div>

            <div className="admin-form-row">
              <label>Descrição / Nota do Acervo:</label>
              <textarea
                className="admin-textarea"
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Detalhes históricos ou táticos sobre esta classe."
              />
            </div>
          </div>

          {/* Caixa 2: Atributos Base */}
          <div className="admin-section-box">
            <h3 className="admin-section-title">Atributos Base (Bases)</h3>
            <p className="admin-helper-text">
              Valores iniciais aplicados a personagens recém-nascidos nesta classe.
            </p>
            <div className="admin-stats-grid">
              {(Object.keys(STAT_LABELS) as (keyof Stats)[]).map((st) => (
                <div key={st} className="admin-stat-field">
                  <span className="admin-stat-tag" style={{ borderLeftColor: STAT_COLORS[st] }}>
                    {st.toUpperCase()}
                  </span>
                  <input
                    type="number"
                    min="0"
                    max="60"
                    className="admin-stat-input"
                    value={bases[st] ?? 0}
                    onChange={(e) => handleBaseChange(st, Number(e.target.value))}
                  />
                  <span className="admin-stat-label-small">{STAT_LABELS[st].split(' ')[0]}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Caixa 3: Taxas de Crescimento (Growths) */}
          <div className="admin-section-box admin-section-span2">
            <h3 className="admin-section-title">Taxas de Crescimento (Growths %)</h3>
            <p className="admin-helper-text">
              Probabilidade percentual de ganhar +1 ponto no atributo ao subir de nível.
            </p>
            <div className="admin-growths-list">
              {(Object.keys(STAT_LABELS) as (keyof Stats)[]).map((st) => {
                const val = growths[st] ?? 0;
                return (
                  <div key={st} className="growth-row">
                    <span className="growth-name" style={{ color: STAT_COLORS[st] }}>
                      {st.toUpperCase()}
                    </span>
                    <input
                      type="range"
                      min="0"
                      max="120"
                      value={val}
                      onChange={(e) => handleGrowthChange(st, Number(e.target.value))}
                      className="growth-range-slider"
                    />
                    <div className="growth-bar-track">
                      <div
                        className="growth-bar-fill"
                        style={{
                          width: `${Math.min(100, val)}%`,
                          backgroundColor: STAT_COLORS[st],
                        }}
                      />
                    </div>
                    <span className="growth-value-box">{val}%</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
